/**
 * Graph-card renderer — chat-panel fallback for animate_graph_path.
 *
 * The AI fires animate_graph_path when it wants to visualize a path
 * across the member network. Pages with a registered graph surface
 * (NetworkAnalyzerPage on /members/:bioguide/network) animate it on
 * the d3 viz. Pages WITHOUT a graph surface render this card in chat
 * instead — a node-and-edge breadcrumb so the user still sees what
 * was traversed.
 *
 * Visual: a chain of node chips connected by arrows, with optional
 * edge labels. Nodes flagged `highlight=true` glow. Counts of nodes
 * + edges + path-hops summarize the structure below.
 */

import { registerRenderer, type RendererProps } from "./registry.js";
import { ArrowRight, Network } from "lucide-react";

export interface GraphNode {
  id: string;
  label?: string;
  highlight?: boolean;
}

export interface GraphEdge {
  source_id: string;
  target_id: string;
  label?: string;
}

export interface GraphCardInput {
  title?: string;
  nodes?: GraphNode[];
  edges?: GraphEdge[];
  /** Ordered list of node ids representing the path. */
  highlight_path?: string[];
}

function GraphCard({ input }: RendererProps<GraphCardInput>) {
  const nodes = Array.isArray(input.nodes) ? input.nodes : [];
  const edges = Array.isArray(input.edges) ? input.edges : [];
  const path = Array.isArray(input.highlight_path) ? input.highlight_path : [];
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const edgeBetween = (a: string, b: string) =>
    edges.find(
      (e) =>
        (e.source_id === a && e.target_id === b) ||
        (e.source_id === b && e.target_id === a)
    );

  return (
    <div
      className="self-stretch border border-stone-200 rounded-xl shadow-card bg-white p-3 flex flex-col gap-2.5"
    >
      <div className="flex items-center gap-2">
        <Network size={15} strokeWidth={1.75} className="text-stone-500 shrink-0" aria-hidden />
        <span className="text-sm font-bold text-stone-900">
          {input.title || "Graph traversal"}
        </span>
      </div>
      {path.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {path.map((nid, i) => {
            const n = nodeById.get(nid) || { id: nid, label: nid };
            const next = path[i + 1];
            const e = next ? edgeBetween(nid, next) : null;
            return (
              <span key={i} className="flex items-center gap-1.5">
                <span
                  className={`px-2 py-0.5 rounded-md border text-xs ${n.highlight ? "bg-accent/60 border-accent text-ink font-semibold" : "bg-stone-50 border-stone-200 text-stone-800"}`}
                  style={{ animationDelay: `${i * 120}ms` }}
                >
                  {n.label || nid}
                </span>
                {next && (
                  <>
                    <ArrowRight size={12} className="text-stone-400" aria-hidden />
                    {e?.label && (
                      <span className="text-[10px] italic text-stone-500">
                        {e.label}
                      </span>
                    )}
                  </>
                )}
              </span>
            );
          })}
        </div>
      )}
      <div
        className="grid gap-x-3 gap-y-0.5 text-[11px] text-stone-600"
        style={{ gridTemplateColumns: "auto 1fr" }}
      >
        <div className="font-semibold text-stone-500">Nodes:</div>
        <div className="font-mono">{nodes.length}</div>
        <div className="font-semibold text-stone-500">Edges:</div>
        <div className="font-mono">{edges.length}</div>
        {path.length > 0 && (
          <>
            <div className="font-semibold text-stone-500">Path:</div>
            <div className="font-mono">
              {Math.max(0, path.length - 1)} hop{path.length === 2 ? "" : "s"}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

registerRenderer<GraphCardInput>({
  tool: "animate_graph_path",
  label: "Graph traversal breadcrumb (chat fallback)",
  Component: GraphCard,
});

export { GraphCard };
